import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  AlertTriangle,
  Bell,
  X,
  ExternalLink,
  Volume2,
  VolumeX,
  Check,
  Frown,
  MessageSquare,
  RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Sound for notifications
const playAlertSound = () => {
  try {
    const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp6clYx9aF1kc4OUo6abkH9tYmhzg5OfoZqNfm1laHODlJ+gmI19bWVpc4KUn5+YjX1sZWlzgpSfn5iNfWxlaXOClJ+fmI19bGVpc4KUn5+YjX1sZWlzgpSfn5iNfGxlaXOClJ+fmI18bGVpc4KUn5+YjXxsZWlzgpSfn5iNfGxlaXOClJ+fmI18bGVpc4KUn5+YjXxsZWlzgpSfn5iNfGxlaXOClJ+fmI18bGVpc4KUn5+YjXxsZQ==");
    audio.volume = 0.4;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

export default function EncuestasAlertasNotifications() {
  const [alertas, setAlertas] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const previousCountRef = useRef(0);
  const wsRef = useRef(null);

  // Fetch alertas from backend
  const fetchAlertas = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/encuestas/portal/alertas/historial?limite=20`, getAuthHeaders());
      const nuevasAlertas = res.data || [];
      
      // Contar alertas de las últimas 24 horas como "no leídas"
      const ahora = new Date();
      const hace24h = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
      const alertasRecientes = nuevasAlertas.filter(a => new Date(a.created_at) > hace24h);
      
      // Si hay nuevas alertas, reproducir sonido
      if (soundEnabled && alertasRecientes.length > previousCountRef.current) {
        playAlertSound();
        
        // Mostrar notificación del navegador
        if (typeof Notification !== 'undefined' && Notification.permission === "granted" && alertasRecientes.length > 0) {
          const ultima = alertasRecientes[0];
          new Notification("🚨 Alerta de Encuesta NPS", {
            body: `${ultima.cliente_nombre}: Comentario negativo detectado`,
            icon: "/favicon.ico",
            tag: "encuesta-alerta"
          });
        }
      }
      
      previousCountRef.current = alertasRecientes.length;
      setUnreadCount(alertasRecientes.length);
      setAlertas(nuevasAlertas);
    } catch (err) {
      console.error("Error fetching alertas:", err);
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, soundEnabled]);

  // Conectar WebSocket para notificaciones en tiempo real
  useEffect(() => {
    if (!user) return;

    // Solicitar permisos de notificación
    if (typeof Notification !== 'undefined' && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Conectar WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = API.replace('https://', '').replace('http://', '').replace('/api', '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/${user.id}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log("WebSocket conectado para alertas de encuestas");
        // Unirse a la room de alertas
        wsRef.current.send(JSON.stringify({
          action: "join_room",
          room_id: "encuestas_alertas"
        }));
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const mensaje = JSON.parse(event.data);
          
          if (mensaje.type === "encuesta_alerta") {
            // Nueva alerta recibida en tiempo real
            const nuevaAlerta = mensaje.data;
            
            setAlertas(prev => [nuevaAlerta, ...prev.slice(0, 19)]);
            setUnreadCount(prev => prev + 1);
            
            if (soundEnabled) {
              playAlertSound();
            }
            
            // Notificación del navegador
            if (typeof Notification !== 'undefined' && Notification.permission === "granted") {
              new Notification("🚨 Alerta de Encuesta NPS", {
                body: `${nuevaAlerta.cliente_nombre}: ${nuevaAlerta.resumen || 'Comentario negativo detectado'}`,
                icon: "/favicon.ico",
                tag: `encuesta-alerta-${nuevaAlerta.id}`
              });
            }
            
            // Toast notification
            toast.error(
              <div className="flex items-start gap-2">
                <Frown className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Comentario Negativo Detectado</p>
                  <p className="text-sm text-muted-foreground">{nuevaAlerta.cliente_nombre} - NPS: {nuevaAlerta.nps_score}</p>
                </div>
              </div>,
              {
                duration: 8000,
                action: {
                  label: "Ver",
                  onClick: () => navigate("/encuestas/resultados")
                }
              }
            );
          }
        } catch (e) {
          console.error("Error procesando mensaje WebSocket:", e);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
      
      wsRef.current.onclose = () => {
        console.log("WebSocket desconectado");
      };
    } catch (e) {
      console.error("Error conectando WebSocket:", e);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, soundEnabled, navigate]);

  // Polling cada 30 segundos como fallback
  useEffect(() => {
    fetchAlertas();
    const interval = setInterval(fetchAlertas, 30000);
    return () => clearInterval(interval);
  }, [fetchAlertas]);

  const handleMarkAsRead = () => {
    setUnreadCount(0);
    previousCountRef.current = alertas.length;
  };

  const handleGoToResults = () => {
    setIsOpen(false);
    navigate("/encuestas/resultados");
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="encuestas-alertas-button"
        >
          <AlertTriangle className={`w-5 h-5 ${unreadCount > 0 ? 'text-red-500' : ''}`} />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b bg-red-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-900">Alertas de Encuestas</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? "Silenciar" : "Activar sonido"}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={fetchAlertas}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleMarkAsRead}
                title="Marcar como leídas"
              >
                <Check className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-[350px]">
          {alertas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="font-medium text-green-700">Sin alertas</p>
              <p className="text-sm text-muted-foreground">
                No hay comentarios negativos recientes
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {alertas.map((alerta, idx) => {
                const esReciente = new Date(alerta.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);
                return (
                  <div
                    key={alerta.id || idx}
                    className={`p-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                      esReciente ? 'bg-red-50/50' : ''
                    }`}
                    onClick={handleGoToResults}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Frown className="w-4 h-4 text-red-500" />
                        <span className="font-medium text-sm truncate max-w-[180px]">
                          {alerta.cliente_nombre}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="text-xs">
                          NPS: {alerta.nps_score}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(alerta.created_at)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-1">
                      "{alerta.comentario}"
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {alerta.campana_nombre}
                      </span>
                      {alerta.sentimiento?.requiere_atencion && (
                        <Badge variant="outline" className="text-xs border-red-200 text-red-600">
                          Requiere Atención
                        </Badge>
                      )}
                    </div>
                    {alerta.sentimiento?.palabras_clave?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {alerta.sentimiento.palabras_clave.slice(0, 3).map((palabra, i) => (
                          <Badge key={i} variant="secondary" className="text-xs bg-red-100 text-red-700">
                            {palabra}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        
        <div className="p-2 border-t bg-slate-50">
          <Button 
            variant="outline" 
            className="w-full" 
            size="sm"
            onClick={handleGoToResults}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Ver todos los comentarios
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
